import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Select, Spin, message } from 'antd';
import { LeftOutlined, RightOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment-jalaali';
import 'moment-jalaali';
import { myCalendarApi } from '../../utils/api';
import { normalizeEnglishDigits } from '../../pages/reports/reportUtils';
import { getPersianHolidaysMap } from '../../utils/persianHolidaysCache';
import { FRIDAY_LABEL } from '../../utils/persianHolidayDates';
import CalendarDayDrawer from './CalendarDayDrawer';

const { Option } = Select;

const WEEK_DAYS = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];

const J_MONTHS = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
];

const toJalaliKey = (m) => normalizeEnglishDigits(m.format('jYYYY-jMM-jDD'));

const CalendarView = () => {
  const [currentMonth, setCurrentMonth] = useState(() => moment().startOf('jMonth'));
  const [filterType, setFilterType] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [holidayMap, setHolidayMap] = useState(new Map());
  const [typesMeta, setTypesMeta] = useState({
    task: 'وظایف',
    events: 'رویدادها',
    tickets: 'تیکت‌ها',
    leaves: 'مرخصی‌ها',
    follow_ups: 'پیگیری‌ها',
  });
  const [dayDrawer, setDayDrawer] = useState({ open: false, date: null });

  useEffect(() => {
    getPersianHolidaysMap().then(setHolidayMap);
  }, []);

  const gridRange = useMemo(() => {
    const monthStart = currentMonth.clone().startOf('jMonth');
    const startDay = monthStart.day();
    const daysFromSat = startDay === 6 ? 0 : startDay + 1;
    const gridStart = monthStart.clone().subtract(daysFromSat, 'days');
    const days = [];
    for (let i = 0; i < 42; i += 1) {
      days.push(gridStart.clone().add(i, 'days'));
    }
    const gridEnd = days[days.length - 1];
    return {
      days,
      startDate: toJalaliKey(gridStart),
      endDate: toJalaliKey(gridEnd),
    };
  }, [currentMonth]);

  const fetchEvents = useCallback(async ({ startDate, endDate, type }) => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const res = await myCalendarApi.getEvents({ startDate, endDate, type });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setEvents(rows);
      if (res?.meta?.types && typeof res.meta.types === 'object') {
        setTypesMeta(res.meta.types);
      }
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت رویدادهای تقویم');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents({
      startDate: gridRange.startDate,
      endDate: gridRange.endDate,
      type: filterType,
    });
  }, [gridRange.startDate, gridRange.endDate, filterType, fetchEvents]);

  const eventsByDate = useMemo(() => {
    const map = new Map();
    events.forEach((item) => {
      if (!item.start) return;
      const key = toJalaliKey(moment(item.start));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return map;
  }, [events]);

  const typeOptions = useMemo(
    () => [
      { value: 'all', label: 'همه' },
      ...Object.entries(typesMeta).map(([key, label]) => ({ value: key, label })),
    ],
    [typesMeta],
  );

  const handlePrev = () => setCurrentMonth((m) => m.clone().subtract(1, 'jMonth'));
  const handleNext = () => setCurrentMonth((m) => m.clone().add(1, 'jMonth'));
  const handleToday = () => setCurrentMonth(moment().startOf('jMonth'));

  const openDay = (dayMoment) => {
    setDayDrawer({ open: true, date: toJalaliKey(dayMoment) });
  };

  const todayKey = toJalaliKey(moment());
  const headerTitle = `${currentMonth.jYear()} ${J_MONTHS[currentMonth.jMonth()]}`;

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
      style={{ fontFamily: 'CustomFont' }}
    >
      {/* هدر */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-l from-slate-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <CalendarOutlined className="text-blue-600 dark:text-blue-400 text-lg" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 m-0">تقویم من</h2>
              <p className="text-xs text-gray-500 m-0">روی هر روز کلیک کنید تا جزئیات را ببینید</p>
            </div>
          </div>

          <Select
            value={filterType}
            onChange={setFilterType}
            className="min-w-[140px]"
            style={{ fontFamily: 'CustomFont' }}
          >
            {typeOptions.map((op) => (
              <Option key={op.value} value={op.value}>{op.label}</Option>
            ))}
          </Select>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-1">
            <Button icon={<RightOutlined />} onClick={handlePrev} />
            <Button onClick={handleToday} style={{ fontFamily: 'CustomFont' }}>
              امروز
            </Button>
            <Button icon={<LeftOutlined />} onClick={handleNext} />
          </div>
          <div className="text-xl font-bold text-gray-800 dark:text-gray-100">{headerTitle}</div>
          <div className="hidden sm:flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-red-50 border border-red-200" />
              تعطیل رسمی
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-amber-50 border border-amber-200" />
              جمعه
            </span>
          </div>
        </div>
      </div>

      <Spin spinning={loading}>
        {/* روزهای هفته */}
        <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          {WEEK_DAYS.map((name) => (
            <div
              key={name}
              className={`py-2 text-center text-xs font-semibold ${
                name === 'جمعه' ? 'text-amber-700 dark:text-amber-400' : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {name}
            </div>
          ))}
        </div>

        {/* شبکه روزها */}
        <div className="grid grid-cols-7">
          {gridRange.days.map((day) => {
            const key = toJalaliKey(day);
            const inMonth = day.jMonth() === currentMonth.jMonth();
            const isToday = key === todayKey;
            const isFriday = day.day() === 5;
            const holidayTitle = holidayMap.get(key);
            const dayEvents = eventsByDate.get(key) || [];
            const visibleEvents = dayEvents.slice(0, 2);
            const moreCount = dayEvents.length - visibleEvents.length;

            let cellBg = 'bg-white dark:bg-gray-800';
            if (!inMonth) cellBg = 'bg-gray-50/80 dark:bg-gray-900/30';
            else if (holidayTitle) cellBg = 'bg-red-50/60 dark:bg-red-950/20';
            else if (isFriday) cellBg = 'bg-amber-50/50 dark:bg-amber-950/15';

            return (
              <button
                key={key}
                type="button"
                onClick={() => openDay(day)}
                title={holidayTitle || (isFriday ? FRIDAY_LABEL : undefined)}
                className={`
                  min-h-[88px] sm:min-h-[110px] p-1.5 sm:p-2 border-l border-b border-gray-100 dark:border-gray-700
                  text-right transition-colors hover:bg-blue-50/50 dark:hover:bg-blue-900/10
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-inset
                  ${cellBg}
                `}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`
                      inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-semibold
                      ${isToday
                        ? 'bg-blue-600 text-white shadow-sm'
                        : inMonth
                          ? 'text-gray-800 dark:text-gray-100'
                          : 'text-gray-400'
                      }
                    `}
                  >
                    {day.jDate()}
                  </span>
                  {dayEvents.length > 0 ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-medium">
                      {dayEvents.length}
                    </span>
                  ) : null}
                </div>

                <div className="space-y-0.5">
                  {visibleEvents.map((ev) => (
                    <div
                      key={`${ev.id}-${ev.start}`}
                      className="text-[10px] sm:text-[11px] leading-tight px-1.5 py-0.5 rounded truncate text-white"
                      style={{
                        backgroundColor: ev?.extendedProps?.bg_color || '#3b82f6',
                      }}
                    >
                      {ev.title}
                    </div>
                  ))}
                  {moreCount > 0 ? (
                    <div className="text-[10px] text-blue-600 dark:text-blue-400 font-medium px-1">
                      +{moreCount} مورد دیگر
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </Spin>

      <CalendarDayDrawer
        open={dayDrawer.open}
        jalaliDate={dayDrawer.date}
        filterType={filterType}
        typesMeta={typesMeta}
        onClose={() => setDayDrawer({ open: false, date: null })}
      />
    </div>
  );
};

export default CalendarView;
